// =====================================================================
// PropertyDetail.jsx — /operational/:location shell
// =====================================================================
// Owns: data fetching + FY selection state. The hero, FY cards, charts,
// monthly table, and YoY growth panel live in dedicated child modules
// under `./property-detail/*` (iter-35e split).
// =====================================================================

import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { logError } from "../lib/log";
import { ArrowLeft } from "lucide-react";

import PropertyDetailHero from "./property-detail/PropertyDetailHero";
import PropertyDetailFyCards from "./property-detail/PropertyDetailFyCards";
import PropertyDetailTable from "./property-detail/PropertyDetailTable";
import {
  MonthlyPerformanceChart, AdrYoyDuoChart,
  RevenueMixCostBedChart, YoyGrowthChart,
} from "./property-detail/PropertyDetailCharts";

const FY_COLORS = { "FY 22-23": "#9370DB", "FY 23-24": "#20B2AA", "FY 24-25": "#FF69B4", "FY 25-26": "#FFCC00" };
const MONTH_LABELS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

export default function PropertyDetail() {
  const { location } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activeFY, setActiveFY] = useState("FY 25-26");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/mis/properties/${encodeURIComponent(location)}`);
        setData(data);
      } catch (e) { logError("loadDetail failed", e); }
      finally { setLoading(false); }
    })();
  }, [location]);

  // Build YoY matrix across all FYs by month index (Apr → Mar).
  // Memo-keyed on `data` since the matrix is identical for any FY tab selection.
  const yoyData = useMemo(() => {
    if (!data?.fy_data) return [];
    const fys = Object.keys(data.fy_data).sort();
    const out = [];
    for (let i = 0; i < 12; i++) {
      const row = { month: MONTH_LABELS[i] };
      fys.forEach((fy) => {
        const m = data.fy_data[fy].find((x) => ((x.month - 4 + 12) % 12) === i);
        row[fy] = m ? m.occupancy_pct : null;
        row[`${fy}_rev`] = m ? Math.round(m.total_revenue / 100000) : null;
      });
      out.push(row);
    }
    return out;
  }, [data]);

  if (loading) {
    return (
      <AppShell title="Loading…">
        <div className="font-mono-tech text-sm text-zinc-500">Fetching property data…</div>
      </AppShell>
    );
  }
  if (!data) {
    return (
      <AppShell title="Not Found">
        <div className="bg-red-50 border-2 border-red-200 p-4">
          <div className="font-bold text-red-700">Property not found</div>
          <button onClick={() => navigate("/dashboard")} className="font-mono-tech text-xs underline mt-2">Back to dashboard</button>
        </div>
      </AppShell>
    );
  }

  const fys = Object.keys(data.fy_data).sort();
  const months = data.fy_data[activeFY] || [];
  const latest = data.latest;
  const summary = data.fy_summary;

  return (
    <AppShell title={location} subtitle={`${data.type} · ${data.model} · live since ${data.start_date?.slice(0, 7) || "—"}`}>
      <button
        onClick={() => navigate("/dashboard")}
        data-testid="back-btn"
        className="flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-zinc-500 hover:text-brand-ink mb-4"
      >
        <ArrowLeft className="w-3 h-3" /> Back to dashboard
      </button>

      <PropertyDetailHero location={location} data={data} latest={latest} />

      <PropertyDetailFyCards
        summary={summary}
        fys={fys}
        activeFY={activeFY}
        setActiveFY={setActiveFY}
        fyColors={FY_COLORS}
      />

      <MonthlyPerformanceChart months={months} activeFY={activeFY} />

      <AdrYoyDuoChart
        months={months}
        activeFY={activeFY}
        yoyData={yoyData}
        fys={fys}
        fyColors={FY_COLORS}
      />

      <PropertyDetailTable months={months} activeFY={activeFY} />

      <YoyGrowthChart summary={summary} activeFY={activeFY} fys={fys} />

      <RevenueMixCostBedChart months={months} activeFY={activeFY} />
    </AppShell>
  );
}
